import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FiTrash2, FiUploadCloud } from "react-icons/fi";
import GenericModal from "../components/GenericModal";
import Loader from "../../components/loader/Loader";
import {
  MAX_IMAGE_SIZE_MB,
  MAX_TOPIC_EXPLANATION,
  MAX_TOPIC_NAME,
  MAX_VIDEO_SIZE_MB,
  TOPIC_NAME_PATTERN,
  flattenMappedActivityIds,
  formatFileSize,
  mappingSelectedCount,
  normalizeActivityMapping,
} from "./helpManualData";
import {
  HELP_MANUAL_COLORS,
  HELP_MANUAL_FONT_SIZES,
  HELP_MANUAL_FONTS,
  helpManualQuillFormats,
} from "./helpManualQuill";
import HelpManualSelect from "./HelpManualSelect";
import HelpManualVideoPlayer from "./HelpManualVideoPlayer";
import ModuleActivityMapping from "./ModuleActivityMapping";
import {
  addHelpManualTopic,
  getActivityAndModuleMaster,
  getHelpManualSectionList,
  getHelpManualTopicById,
  getHelpManualTopicDropdown,
  getSectionAndTopicMaster,
  mapSectionListOptions,
  mapActivityAndModuleMaster,
  mapSectionAndTopicMaster,
  mapTopicDropdownOptions,
  updateHelpManualTopic,
} from "./helpManualApi";

const MAX_IMAGE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;
const ALLOWED_IMAGE_MIME = ["image/jpeg", "image/png"];

const HelpManualQuillToolbar = memo(function HelpManualQuillToolbar({
  instanceId,
  colorHandlerRef,
}) {
  return (
    <div id="help-manual-toolbar" className="ql-toolbar ql-snow" data-instance={instanceId}>
      <span className="ql-formats">
        <button type="button" className="ql-bold" />
        <button type="button" className="ql-italic" />
        <button type="button" className="ql-header" value="2" />
        <button type="button" className="ql-list" value="ordered" />
        <button type="button" className="ql-list" value="bullet" />
      </span>
      <span className="ql-formats">
        <select className="ql-font">
          <option value="" />
          {HELP_MANUAL_FONTS.map((font) => (
            <option key={font.value} value={font.value}>
              {font.label}
            </option>
          ))}
        </select>
        <select className="ql-size">
          <option value="" />
          {HELP_MANUAL_FONT_SIZES.map((size) => (
            <option key={size.value} value={size.value}>
              {size.label}
            </option>
          ))}
        </select>
      </span>
      <span className="ql-formats help-manual-toolbar-colors">
        {HELP_MANUAL_COLORS.map((color) => (
          <button
            key={color.value}
            type="button"
            className="help-manual-color-swatch"
            style={{ backgroundColor: color.value }}
            title={color.label}
            aria-label={color.label}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => colorHandlerRef.current?.(color.value)}
          />
        ))}
      </span>
      <span className="ql-formats">
        <button type="button" className="ql-link" />
        <button type="button" className="ql-image" />
      </span>
    </div>
  );
});

const sanitizeExplanationHtml = (html) => {
  if (!html) return html;
  return html
    .replace(/href\s*=\s*(["'])\s*javascript:[^"']*\1/gi, 'href="#"')
    .replace(/href\s*=\s*javascript:[^\s>]+/gi, 'href="#"');
};

const readFileHeader = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = reject;
    reader.readAsArrayBuffer(file.slice(0, 8));
  });

const isJpegOrPngHeader = (bytes) => {
  const jpeg = bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
  const png =
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47;
  return jpeg || png;
};

const decodeImageFile = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(true);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("corrupt"));
    };
    img.src = url;
  });

const TopicForm = () => {
  const navigate = useNavigate();
  const { sectionId, topicId } = useParams();
  const numericSectionId = Number(sectionId);
  const numericTopicId = topicId ? Number(topicId) : 0;
  const isEdit = Boolean(topicId);

  const goToList = () => navigate("/helpmanual");

  const fileInputRef = useRef(null);
  const uploadTimerRef = useRef(null);
  const uploadTokenRef = useRef(0);
  const quillRef = useRef(null);
  const applyEditorColorRef = useRef(null);
  const setErrorsRef = useRef(null);
  const savingRef = useRef(false);
  const bootstrappedRef = useRef(false);

  const [sectionName, setSectionName] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState(() =>
    String(numericSectionId || "")
  );
  const [sectionOptions, setSectionOptions] = useState([]);
  const initialPlaceAfterRef = useRef("0");
  const [placeOptions, setPlaceOptions] = useState([
    { value: "0", label: "Start" },
  ]);
  const [moduleTree, setModuleTree] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [name, setName] = useState("");
  const [explanation, setExplanation] = useState("");
  const [placeAfter, setPlaceAfter] = useState("");
  const [activityIds, setActivityIds] = useState(() =>
    normalizeActivityMapping()
  );
  const [video, setVideo] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [existingVideoPath, setExistingVideoPath] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [errors, setErrors] = useState({});
  const [editorReady, setEditorReady] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [infoModal, setInfoModal] = useState({
    open: false,
    title: "",
    description: "",
    type: "success",
  });
  const [linkPrompt, setLinkPrompt] = useState(null);
  const [linkError, setLinkError] = useState("");
  const setLinkPromptRef = useRef(null);

  setErrorsRef.current = setErrors;
  setLinkPromptRef.current = setLinkPrompt;

  const showError = (message) => {
    setInfoModal({
      open: true,
      title: "Error",
      description: message || "Something went wrong. Please try again.",
      type: "error",
    });
  };

  const looksLikeUrl = (value) =>
    /^(https?:\/\/|mailto:)/i.test(String(value || "").trim());

  const normalizeLinkUrl = (raw) => {
    const value = String(raw || "").trim();
    if (!value) return "";
    if (/^javascript:/i.test(value) || /^data:/i.test(value)) return "";
    if (looksLikeUrl(value)) return value;
    return `https://${value}`;
  };

  const classifyActivityIds = useCallback((rawIds, modules) => {
    const ids = String(rawIds || "")
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean)
      .map(String);

    const enterpriseIds = new Set();
    const siteIds = new Set();
    (modules || []).forEach((module) => {
      const typeId = module.clientTypeId;
      (module.activities || []).forEach((activity) => {
        const activityId = String(activity.id);
        if (typeId == null || Number(typeId) === 1) {
          enterpriseIds.add(activityId);
        }
        if (typeId == null || Number(typeId) === 2) {
          siteIds.add(activityId);
        }
      });
    });

    return normalizeActivityMapping({
      enterprise: ids.filter(
        (id) => enterpriseIds.has(id) || (!enterpriseIds.has(id) && !siteIds.has(id))
      ),
      site: ids.filter((id) => siteIds.has(id) && !enterpriseIds.has(id)),
    });
  }, []);

  const loadForm = useCallback(async () => {
    if (!numericSectionId || Number.isNaN(numericSectionId)) {
      goToList();
      return;
    }

    setIsLoading(true);
    try {
      const requests = [
        getSectionAndTopicMaster(),
        getHelpManualTopicDropdown({
          sectionId: numericSectionId,
          excludeTopicId: isEdit ? numericTopicId : null,
        }),
        getActivityAndModuleMaster(null),
      ];
      if (isEdit) {
        requests.push(getHelpManualSectionList());
      }
      const [tocData, dropdownData, activityData, sectionListData] =
        await Promise.all(requests);

      const sections = mapSectionAndTopicMaster(tocData);
      const section = sections.find((s) => Number(s.id) === numericSectionId);
      if (!section) {
        showError("Section was not found or is inactive.");
        return;
      }
      setSectionName(section.name);
      if (isEdit) {
        const fromList = mapSectionListOptions(sectionListData);
        const fromToc = sections
          .map((item) => ({
            value: String(item.id),
            label: item.name,
          }))
          .filter((option) => option.value && option.label);
        const listed = fromList.length > 0 ? fromList : fromToc;
        const currentId = String(numericSectionId);
        setSectionOptions(
          listed.some((option) => option.value === currentId)
            ? listed
            : [{ value: currentId, label: section.name }, ...listed]
        );
        setSelectedSectionId(currentId);
      }

      const options = mapTopicDropdownOptions(dropdownData);
      setPlaceOptions(
        options.length > 0 ? options : [{ value: "0", label: "Start" }]
      );

      const modules = mapActivityAndModuleMaster(activityData);
      setModuleTree(modules);

      if (isEdit) {
        if (!numericTopicId || Number.isNaN(numericTopicId)) {
          goToList();
          return;
        }
        const topicResult = await getHelpManualTopicById(numericTopicId);
        const statusCode = topicResult.statusCode ?? topicResult.StatusCode;
        const details = topicResult.data ?? topicResult.Data;
        if (statusCode !== 1 || !details) {
          showError(
            topicResult.message ||
              topicResult.Message ||
              "Failed to load topic details."
          );
          return;
        }

        const detailSectionId = details.sectionId ?? details.SectionId;
        if (Number(detailSectionId) !== numericSectionId) {
          showError("Topic does not belong to this section.");
          return;
        }

        setName(details.name ?? details.Name ?? "");
        setExplanation(details.description ?? details.Description ?? "");
        const placeAfterId = String(
          details.placeAfterTopicId ?? details.PlaceAfterTopicId ?? 0
        );
        initialPlaceAfterRef.current = placeAfterId;
        setPlaceAfter(placeAfterId);
        setActivityIds(
          classifyActivityIds(
            details.activityIds ?? details.ActivityIds ?? "",
            modules
          )
        );

        const videoPath = details.videoPath ?? details.VideoPath ?? null;
        setExistingVideoPath(videoPath);
        setVideoFile(null);
        if (videoPath) {
          const cleaned = String(videoPath).split("?")[0];
          const parts = cleaned.split(/[/\\]/);
          setVideo({
            name: parts[parts.length - 1] || cleaned,
            sizeLabel: "",
            ready: true,
            url: videoPath,
          });
        } else {
          setVideo(null);
        }
      } else {
        setName("");
        setExplanation("");
        initialPlaceAfterRef.current = "0";
        setPlaceAfter("");
        setActivityIds(normalizeActivityMapping());
        setVideo(null);
        setVideoFile(null);
        setExistingVideoPath(null);
      }

      setErrors({});
      setUploadProgress(null);
    } catch (err) {
      console.error("TopicForm load error:", err);
      if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
        showError(
          "Unable to connect to server. Please check your internet connection."
        );
      } else {
        showError(
          err.response?.data?.message ||
            err.response?.data?.Message ||
            "Failed to load topic form."
        );
      }
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classifyActivityIds, isEdit, numericSectionId, numericTopicId]);

  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    loadForm();
  }, [loadForm]);

  const ensureStartOption = (options) => {
    const rest = (options || []).filter((option) => option.value !== "0");
    return [{ value: "0", label: "Start" }, ...rest];
  };

  const handleSectionChange = async (value) => {
    const nextSectionId = Number(value);
    if (!nextSectionId || Number.isNaN(nextSectionId)) return;

    setSelectedSectionId(String(nextSectionId));
    setErrors((prev) => ({ ...prev, placeAfter: "" }));
    setIsLoading(true);
    try {
      const dropdownData = await getHelpManualTopicDropdown({
        sectionId: nextSectionId,
        excludeTopicId:
          nextSectionId === numericSectionId ? numericTopicId : null,
      });
      const options = ensureStartOption(mapTopicDropdownOptions(dropdownData));
      setPlaceOptions(options);

      if (nextSectionId === numericSectionId) {
        const original = initialPlaceAfterRef.current || "0";
        setPlaceAfter(
          options.some((option) => option.value === original) ? original : "0"
        );
      } else {
        setPlaceAfter("0");
      }
    } catch (err) {
      console.error("Topic section change error:", err);
      showError(
        err.response?.data?.message ||
          err.response?.data?.Message ||
          "Failed to load topics for the selected section."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: "#help-manual-toolbar",
        handlers: {
          link() {
            const quill = quillRef.current?.getEditor?.();
            if (!quill) return;
            quill.focus();
            const range =
              quill.getSelection() ||
              quill.selection?.savedRange || {
                index: Math.max(0, quill.getLength() - 1),
                length: 0,
              };
            const selected = range.length ? quill.getText(range).trim() : "";
            const existing = range.length ? quill.getFormat(range).link : "";
            setLinkError("");
            setLinkPromptRef.current?.({
              url: existing || (looksLikeUrl(selected) ? selected : ""),
              text: looksLikeUrl(selected) ? "" : selected,
              range,
              isEdit: Boolean(existing),
            });
          },
          image() {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = "image/jpeg,image/png,.jpg,.jpeg,.png";
            input.onchange = async () => {
              const file = input.files?.[0];
              if (!file) return;
              const report = (msg) =>
                setErrorsRef.current?.((prev) => ({
                  ...prev,
                  explanation: msg,
                }));
              const looksLikeImage =
                ALLOWED_IMAGE_MIME.includes(file.type) ||
                /\.(jpe?g|png)$/i.test(file.name);
              if (!looksLikeImage) {
                report("Only JPG and PNG images are supported.");
                return;
              }
              if (file.size > MAX_IMAGE_BYTES) {
                report(`Maximum image size is ${MAX_IMAGE_SIZE_MB} MB.`);
                return;
              }
              try {
                const header = await readFileHeader(file);
                if (!isJpegOrPngHeader(header)) {
                  report("File content is not a valid JPG or PNG image.");
                  return;
                }
                await decodeImageFile(file);
              } catch {
                report("Image could not be inserted. The file may be corrupt.");
                return;
              }
              const reader = new FileReader();
              reader.onload = () => {
                const quill = quillRef.current?.getEditor?.();
                if (!quill) return;
                const range = quill.getSelection(true) || {
                  index: quill.getLength(),
                };
                quill.insertEmbed(range.index, "image", reader.result, "user");
                quill.setSelection(range.index + 1);
                setErrorsRef.current?.((prev) => ({
                  ...prev,
                  explanation: "",
                }));
              };
              reader.readAsDataURL(file);
            };
            input.click();
          },
        },
      },
    }),
    []
  );

  useEffect(() => {
    return () => {
      uploadTokenRef.current += 1;
      if (uploadTimerRef.current) {
        clearInterval(uploadTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setEditorReady(true);
  }, []);

  const plainExplanationLength = explanation
    .replace(/<[^>]*>/g, "")
    .trim().length;

  const validate = () => {
    const next = {};
    const trimmed = name.trim();
    if (!trimmed) next.name = "Topic name is required.";
    else if (trimmed.length > MAX_TOPIC_NAME)
      next.name = `Topic name must be at most ${MAX_TOPIC_NAME} characters.`;
    else if (!TOPIC_NAME_PATTERN.test(trimmed))
      next.name = "Special characters are not allowed.";

    if (!plainExplanationLength)
      next.explanation = "Topic explanation is required.";
    else if (plainExplanationLength > MAX_TOPIC_EXPLANATION)
      next.explanation = `Explanation must be at most ${MAX_TOPIC_EXPLANATION} characters.`;

    if (mappingSelectedCount(activityIds) === 0)
      next.activities = "At least one activity mapping is required.";

    if (placeAfter === "" || placeAfter === null || placeAfter === undefined) {
      next.placeAfter = "Place after topic is required.";
    }

    if (uploadProgress !== null && uploadProgress < 100) {
      next.video = "Please wait until the video upload is complete.";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const revokeIfBlobUrl = (url) => {
    if (url?.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  };

  const simulateUpload = (file) => {
    const token = ++uploadTokenRef.current;
    const objectUrl = URL.createObjectURL(file);
    if (uploadTimerRef.current) {
      clearInterval(uploadTimerRef.current);
    }
    setUploadProgress(0);
    setVideoFile(file);
    uploadTimerRef.current = setInterval(() => {
      if (token !== uploadTokenRef.current) {
        clearInterval(uploadTimerRef.current);
        uploadTimerRef.current = null;
        URL.revokeObjectURL(objectUrl);
        return;
      }
      setUploadProgress((prev) => {
        const next = (prev ?? 0) + 12;
        if (next >= 100) {
          clearInterval(uploadTimerRef.current);
          uploadTimerRef.current = null;
          if (token !== uploadTokenRef.current) {
            URL.revokeObjectURL(objectUrl);
            return null;
          }
          setVideo((current) => {
            if (current?.url && current.url !== objectUrl) {
              revokeIfBlobUrl(current.url);
            }
            return {
              name: file.name,
              sizeLabel: formatFileSize(file.size),
              ready: true,
              url: objectUrl,
            };
          });
          return 100;
        }
        return next;
      });
    }, 120);
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["mp4", "avi"].includes(ext) || file.size === 0) {
      uploadTokenRef.current += 1;
      setUploadProgress(null);
      setErrors((prev) => ({
        ...prev,
        video: "Only valid .mp4 and .avi files are supported.",
      }));
      e.target.value = "";
      return;
    }
    if (file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
      uploadTokenRef.current += 1;
      setUploadProgress(null);
      setErrors((prev) => ({
        ...prev,
        video: `Maximum file size is ${MAX_VIDEO_SIZE_MB} MB.`,
      }));
      e.target.value = "";
      return;
    }
    setErrors((prev) => ({ ...prev, video: "" }));
    setExistingVideoPath(null);
    simulateUpload(file);
    e.target.value = "";
  };

  const applyEditorColor = (color) => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    quill.focus();
    const range =
      quill.getSelection() ||
      quill.selection?.savedRange || {
        index: Math.max(0, quill.getLength() - 1),
        length: 0,
      };
    quill.setSelection(range.index, range.length, "silent");
    const current = quill.getFormat(range)?.color;
    quill.format("color", current === color ? false : color, "user");
  };
  applyEditorColorRef.current = applyEditorColor;

  const applyLinkPrompt = () => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill || !linkPrompt) return;
    const href = normalizeLinkUrl(linkPrompt.url);
    if (!href) {
      setLinkError("Enter a valid http, https, or mailto URL.");
      return;
    }
    const range = linkPrompt.range || { index: quill.getLength() - 1, length: 0 };
    const display = String(linkPrompt.text || "").trim() || href;
    quill.focus();
    quill.setSelection(range.index, range.length, "silent");
    if (range.length > 0) {
      quill.deleteText(range.index, range.length, "user");
      quill.insertText(range.index, display, { link: href }, "user");
      quill.setSelection(range.index, display.length);
    } else {
      quill.insertText(range.index, display, { link: href }, "user");
      quill.setSelection(range.index, display.length);
    }
    setLinkPrompt(null);
    setLinkError("");
    setErrors((prev) => ({ ...prev, explanation: "" }));
  };

  const closeLinkPrompt = () => {
    setLinkPrompt(null);
    setLinkError("");
  };

  const handleSave = () => {
    if (savingRef.current || isLoading) return;
    closeLinkPrompt();
    if (!validate()) return;
    setShowSaveModal(true);
  };

  const confirmSave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setShowSaveModal(false);
    setIsLoading(true);

    const activityIdCsv = flattenMappedActivityIds(activityIds).join(",");
    const payload = {
      sectionId: isEdit
        ? Number(selectedSectionId) || numericSectionId
        : numericSectionId,
      name: name.trim(),
      description: sanitizeExplanationHtml(explanation),
      placeAfterTopicId: Number(placeAfter) || 0,
      activityIds: activityIdCsv,
      videoFile: videoFile instanceof File ? videoFile : null,
    };

    try {
      const result = isEdit
        ? await updateHelpManualTopic({
            ...payload,
            id: numericTopicId,
            videoPath: videoFile ? null : existingVideoPath,
          })
        : await addHelpManualTopic(payload);

      const statusCode = result.statusCode ?? result.StatusCode;
      const message = result.message ?? result.Message;

      if (statusCode === 1) {
        setInfoModal({
          open: true,
          title: isEdit ? "Topic updated" : "Topic saved",
          description:
            message ||
            `"${name.trim()}" has been ${isEdit ? "updated" : "added"}.`,
          type: "success",
        });
      } else {
        setErrors((prev) => ({
          ...prev,
          name: message || "Failed to save topic.",
        }));
        showError(message || "Failed to save topic.");
      }
    } catch (err) {
      console.error(
        isEdit ? "UpdateHelpManualTopic error:" : "AddHelpManualTopic error:",
        err
      );
      if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
        showError(
          "Unable to connect to server. Please check your internet connection."
        );
      } else {
        const errorMsg =
          err.response?.data?.message ||
          err.response?.data?.Message ||
          "Failed to save topic. Please try again.";
        showError(errorMsg);
      }
    } finally {
      setIsLoading(false);
      savingRef.current = false;
    }
  };

  return (
    <>
      {isLoading && <Loader />}
      <div className="bg-white rounded-lg flex flex-col h-full min-h-0 overflow-hidden">
        <div className="px-6 pt-6 pb-4 shrink-0">
        <div className="mb-0">
          <h1 className="text-xl font-semibold">
            {isEdit ? "Edit Topic" : "Add Topic"}
          </h1>
          {isEdit ? (
            <div className="mt-3 max-w-[720px]">
              <HelpManualSelect
                label="Section"
                value={selectedSectionId}
                options={sectionOptions}
                onChange={handleSectionChange}
                placeholder="Select section"
              />
            </div>
          ) : (
            <p className="text-sm text-primary mt-0.5">
              Target Section: {sectionName || "—"}
            </p>
          )}
          <nav className="text-sm mt-1" aria-label="Breadcrumb">
            <span
              className="text-blue-500 cursor-pointer hover:underline"
              onClick={goToList}
            >
              Help Manual
            </span>
            <span className="mx-1 text-gray-400">&gt;</span>
            <span className="text-primary">
              {isEdit ? "Edit Topic" : "Add Topic"}
            </span>
          </nav>
        </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-6 pb-4">
          <div className="max-w-[720px] space-y-4 min-w-0 pb-2">
          {!isEdit && (
          <div>
            <label className="block text-sm font-medium mb-1">
              Section Name
            </label>
            <input
              type="text"
              value={sectionName}
              disabled
              className="w-full border rounded-md px-3 py-2 text-sm input-field bg-gray-100 cursor-not-allowed opacity-70"
            />
          </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">
              Topic Name <span className="text-error">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setErrors((prev) => ({ ...prev, name: "" }));
              }}
              maxLength={MAX_TOPIC_NAME}
              placeholder="Enter topic name"
              className={`w-full border rounded-md px-3 py-2 text-sm input-field ${
                errors.name ? "input-error" : ""
              }`}
            />
            <div className="min-h-[16px]">
              {errors.name && (
                <p className="text-xs text-red-500 mt-1">{errors.name}</p>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium mb-0">
                Video Attachment
              </label>
              <span className="text-xs text-gray-500">.mp4, .avi</span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp4,.avi,video/mp4,video/x-msvideo"
              className="hidden"
              onChange={handleVideoSelect}
            />
            {video ? (
              <div className="space-y-2 min-w-0">
                <div className="help-manual-upload-preview">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {video.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      File size: {video.sizeLabel} / {MAX_VIDEO_SIZE_MB} MB
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-sm text-primary"
                    >
                      Replace
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        uploadTokenRef.current += 1;
                        revokeIfBlobUrl(video?.url);
                        setVideo(null);
                        setVideoFile(null);
                        setExistingVideoPath(null);
                        setUploadProgress(null);
                      }}
                      className="text-gray-500 hover:text-red-500"
                      title="Remove video"
                      aria-label="Remove video"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </div>
                {video.url && video.ready && (
                  <HelpManualVideoPlayer src={video.url} compact />
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="help-manual-upload-zone"
              >
                <FiUploadCloud className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-700">Click to upload video</p>
                <p className="text-xs text-gray-500 mt-1">
                  Maximum file size: {MAX_VIDEO_SIZE_MB} MB
                </p>
              </button>
            )}
            {uploadProgress !== null && uploadProgress < 100 && (
              <div className="mt-2">
                <div className="help-manual-progress">
                  <div
                    className="help-manual-progress__bar"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Uploading… {uploadProgress}%
                </p>
              </div>
            )}
            {errors.video && (
              <p className="text-xs text-error mt-1">{errors.video}</p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium mb-0">
                Topic Explanation <span className="text-error">*</span>
              </label>
              <span className="text-xs text-gray-400">
                {plainExplanationLength}/{MAX_TOPIC_EXPLANATION}
              </span>
            </div>
            <div
              className={`help-manual-editor rounded-md border min-w-0 overflow-visible ${
                errors.explanation ? "border-red-500" : "border-gray-300"
              }`}
            >
              <HelpManualQuillToolbar
                instanceId={`${sectionId}-${topicId || "add"}`}
                colorHandlerRef={applyEditorColorRef}
              />
              {editorReady && (
                <ReactQuill
                  key={`${sectionId}-${topicId || "add"}`}
                  ref={quillRef}
                  theme="snow"
                  value={explanation}
                  onChange={(value) => {
                    setExplanation(value);
                    setErrors((prev) => ({ ...prev, explanation: "" }));
                  }}
                  modules={quillModules}
                  formats={helpManualQuillFormats}
                  placeholder="Enter topic explanation"
                />
              )}
            </div>
            {errors.explanation && (
              <p className="text-xs text-error mt-1">{errors.explanation}</p>
            )}
          </div>

          <HelpManualSelect
            label="Place after Topic"
            required
            value={placeAfter}
            options={placeOptions}
            onChange={setPlaceAfter}
            error={errors.placeAfter}
          />

          <div>
            <label className="block text-sm font-medium mb-1">
              Modules &amp; Activities Mapping{" "}
              <span className="text-error">*</span>
            </label>
            <ModuleActivityMapping
              selectedIds={activityIds}
              modules={moduleTree}
              onChange={(ids) => {
                setActivityIds(ids);
                setErrors((prev) => ({ ...prev, activities: "" }));
              }}
            />
            {errors.activities && (
              <p className="text-xs text-error mt-1">{errors.activities}</p>
            )}
          </div>
          </div>
        </div>

        <div className="help-manual-form-actions">
          <button
            type="button"
            className="w-[120px]"
            onClick={() => {
              closeLinkPrompt();
              setShowCancelModal(true);
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-save w-[120px]"
            onClick={handleSave}
          >
            {isEdit ? "Update" : "Save"}
          </button>
        </div>
      </div>

      <GenericModal
        open={Boolean(linkPrompt)}
        title={linkPrompt?.isEdit ? "Edit link" : "Insert link"}
        primaryText={linkPrompt?.isEdit ? "Update" : "Insert"}
        secondaryText="Cancel"
        onPrimary={applyLinkPrompt}
        onClose={closeLinkPrompt}
      >
        <label className="block text-sm font-medium mb-1 text-gray-800">
          Link text
          <input
            type="text"
            value={linkPrompt?.text || ""}
            onChange={(e) =>
              setLinkPrompt((prev) =>
                prev ? { ...prev, text: e.target.value } : prev
              )
            }
            placeholder="Display text"
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm input-field"
          />
        </label>
        <label className="block text-sm font-medium mb-1 mt-3 text-gray-800">
          URL
          <input
            type="url"
            value={linkPrompt?.url || ""}
            autoFocus
            onChange={(e) => {
              setLinkError("");
              setLinkPrompt((prev) =>
                prev ? { ...prev, url: e.target.value } : prev
              );
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyLinkPrompt();
              }
            }}
            placeholder="https://example.com"
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm input-field"
            aria-label="Link URL"
          />
        </label>
        {linkError && <p className="text-xs text-error mt-1">{linkError}</p>}
      </GenericModal>

      <GenericModal
        open={showCancelModal}
        title="Confirm Cancel"
        description="Are you sure to cancel and exit?"
        primaryText="Yes"
        secondaryText="No"
        onPrimary={goToList}
        onClose={() => setShowCancelModal(false)}
      />

      <GenericModal
        open={showSaveModal}
        title={isEdit ? "Update Topic" : "Save Topic"}
        description={`Are you sure you want to ${
          isEdit ? "update" : "save"
        } this topic?`}
        primaryText={isEdit ? "Update" : "Save"}
        secondaryText="Cancel"
        onPrimary={confirmSave}
        onClose={() => setShowSaveModal(false)}
      />

      <GenericModal
        open={infoModal.open}
        title={infoModal.title}
        description={infoModal.description}
        primaryText="OK"
        type={infoModal.type || "success"}
        onClose={() => {
          if (infoModal.type === "error") {
            setInfoModal((prev) => ({ ...prev, open: false }));
            return;
          }
          goToList();
        }}
        onPrimary={() => {
          if (infoModal.type === "error") {
            setInfoModal((prev) => ({ ...prev, open: false }));
            return;
          }
          goToList();
        }}
      />
    </>
  );
};

export default TopicForm;
